import { useEffect, useMemo, useRef, useState } from "react";
import { Picker } from "@cntrl/ui";
import { useApp } from "../../../app/appContext.js";
import { useBandContext } from "../../../app/bandContext.js";
import { audienceLabel } from "./targetingFormat.js";
import styles from "./TargetingField.module.scss";

/** One resolved targeting option, as the client stores it — the OPAQUE key plus the words shown beside it. */
export interface TargetingToken {
  key: string;
  name: string;
  /** Cities only: the radius Meta targets around them, in miles (10–80). */
  radiusMiles?: number;
}

interface Choice {
  key: string;
  name: string;
  detail?: string | null;
  audienceSize?: number | null;
}

export const RADIUS_MIN = 10;
export const RADIUS_MAX = 80;
export const RADIUS_DEFAULT = 25;
/** The radius picker is a dropdown of presets across Meta's 10–80 mi range (ART-205, Ryan). */
export const RADIUS_OPTIONS = [10, 15, 25, 40, 50, 65, 80] as const;

/**
 * The audience builder's typeahead — cities, states/regions and interests, all three over the one
 * `/ads/targeting-search` route.
 *
 * **It exists because a free-text box here is not a smaller version of this control, it is a broken one.**
 * Meta's targeting spec takes opaque KEYS: a city is `{"key":"2418779"}`, an interest `{"id":"6003107902433"}`.
 * A typed name in that slot is accepted by our API, sent to Meta, and rejected at ad-set create — a form the
 * user filled in correctly, failing later and somewhere else. The States field shipped exactly that way and
 * this replaces it.
 *
 * Debounced at 250 ms with a 2-character floor, matching the reference; the floor is enforced server-side too,
 * where it costs no vendor call at all.
 */
export function TargetingField({
  label,
  kind,
  hint,
  tokens,
  onChange,
  withRadius,
  radiusMiles,
  onRadiusChange,
  localOptions,
}: {
  label: string;
  kind: "city" | "region" | "interest" | "country";
  hint?: string;
  tokens: TargetingToken[];
  onChange: (next: TargetingToken[]) => void;
  /** Cities carry a radius; regions and interests do not. */
  withRadius?: boolean;
  radiusMiles?: number;
  onRadiusChange?: (miles: number) => void;
  /** When provided, the typeahead filters THIS fixed list locally instead of calling Meta — countries are a
   *  closed set we already know, so there is no vendor round-trip (ART-205, Ryan: "searchable like states"). */
  localOptions?: Choice[];
}) {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const [query, setQuery] = useState("");
  const [choices, setChoices] = useState<Choice[]>([]);
  const [searching, setSearching] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    const term = query.trim();
    // A LOCAL list (countries) filters in place — no debounce, no vendor call. One character is enough here
    // because the set is small and known.
    if (localOptions) {
      const needle = term.toLowerCase();
      setSearching(false);
      setChoices(term ? localOptions.filter((o) => o.name.toLowerCase().includes(needle)).slice(0, 30) : []);
      return;
    }
    if (term.length < 2) {
      setChoices([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    const mine = ++seq.current;
    const t = setTimeout(async () => {
      try {
        const res = await api.GET("/api/v1/bands/{bandId}/ads/targeting-search", {
          params: { path: { bandId }, query: { kind, q: term } },
        });
        // A stale response must never overwrite a newer one — the user keeps typing while a request is in
        // flight, and the shorter query usually answers second.
        if (seq.current !== mine) return;
        setChoices((res.data?.data?.options ?? []) as Choice[]);
      } catch {
        if (seq.current === mine) setChoices([]);
      } finally {
        if (seq.current === mine) setSearching(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [query, kind, api, bandId, localOptions]);

  const selected = useMemo(() => new Set(tokens.map((t) => t.key)), [tokens]);

  const toggle = (c: Choice) => {
    if (selected.has(c.key)) onChange(tokens.filter((t) => t.key !== c.key));
    else onChange([...tokens, { key: c.key, name: c.name, ...(withRadius ? { radiusMiles: radiusMiles ?? RADIUS_DEFAULT } : {}) }]);
  };

  return (
    <div className={styles.field}>
      <div className={styles.labelRow}>
        <span className={styles.label}>{label}</span>
        {withRadius ? (
          <span className={styles.radius}>
            radius
            {/* A dropdown of presets rather than a number box (ART-205) — every option is inside Meta's 10–80 mi
                range, so an out-of-range value cannot be expressed. The house Picker so a phone gets the sheet. */}
            <Picker
              bare
              label={`${label} radius in miles`}
              value={String(radiusMiles ?? RADIUS_DEFAULT)}
              onChange={(v) => onRadiusChange?.(Number(v))}
              options={RADIUS_OPTIONS.map((mi) => ({ value: String(mi), label: `${mi} mi` }))}
              triggerClassName={styles.radiusTrigger}
            />
          </span>
        ) : null}
      </div>

      {tokens.length > 0 ? (
        <ul className={styles.tokens}>
          {tokens.map((t) => (
            <li key={t.key}>
              <button type="button" className={styles.token} onClick={() => onChange(tokens.filter((x) => x.key !== t.key))}>
                {t.name}
                <span aria-hidden="true">×</span>
                <span className={styles.srOnly}>Remove</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      <input
        className={styles.search}
        type="text"
        value={query}
        placeholder={`Search ${label.toLowerCase()}…`}
        aria-label={`Search ${label.toLowerCase()}`}
        autoComplete="off"
        onChange={(e) => setQuery(e.target.value)}
      />
      {hint ? <p className={styles.hint}>{hint}</p> : null}

      {query.trim().length >= (localOptions ? 1 : 2) ? (
        <ul className={styles.choices}>
          {searching && choices.length === 0 ? (
            <li className={styles.choiceEmpty}>Searching…</li>
          ) : choices.length === 0 ? (
            <li className={styles.choiceEmpty}>No matches.</li>
          ) : (
            choices.map((c) => (
              <li key={c.key}>
                <button
                  type="button"
                  className={styles.choice}
                  data-selected={selected.has(c.key) || undefined}
                  onClick={() => toggle(c)}
                >
                  <span className={styles.choiceName}>{c.name}</span>
                  {c.detail ? <span className={styles.choiceDetail}>{c.detail}</span> : null}
                  {c.audienceSize ? <span className={styles.choiceSize}>{audienceLabel(c.audienceSize)}</span> : null}
                </button>
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );
}
